/**
 * 数据缓冲器
 * 职责：批量上报、节流、采样等性能优化
 */

import { TrackEvent } from "../event/EventBus";

export interface BufferOptions {
  size?: number; // 缓冲区大小
  delay?: number; // 延迟上报时间（毫秒）
  throttle?: number; // 节流时间（毫秒）
  sampleRate?: number; // 采样率 [0-1]
}

export class Buffer {
  private buffer: TrackEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastEmitTime: number = 0;
  private options: Required<BufferOptions>;
  private onFlush: (events: TrackEvent[]) => void;

  constructor(
    onFlush: (events: TrackEvent[]) => void,
    options: BufferOptions = {}
  ) {
    this.onFlush = onFlush;
    this.options = {
      size: options.size ?? 30,
      delay: options.delay ?? 3000,
      throttle: options.throttle ?? 0,
      sampleRate: options.sampleRate ?? 1,
    };

    if (process.env.NODE_ENV === "development") {
      console.log("[Buffer] Initialized with options:", this.options);
    }
  }

  /**
   * 添加事件到缓冲区
   */
  add(event: TrackEvent): void {
    // 采样
    if (Math.random() > this.options.sampleRate) {
      if (process.env.NODE_ENV === "development") {
        console.log("[Buffer] Event sampled out:", event.event_name);
      }
      return;
    }

    this.buffer.push(event);

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[Buffer] Added event: ${event.event_name} (buffer size: ${this.buffer.length})`
      );
    }

    // 达到缓冲区大小，立即上报
    if (this.buffer.length >= this.options.size) {
      if (process.env.NODE_ENV === "development") {
        console.log("[Buffer] Buffer size reached, flushing...");
      }
      this.flush();
      return;
    }

    // 启动延迟上报定时器
    if (!this.timer) {
      this.timer = setTimeout(() => {
        if (process.env.NODE_ENV === "development") {
          console.log("[Buffer] Delay timeout, flushing...");
        }
        this.flush();
      }, this.options.delay);
    }
  }

  /**
   * 立即上报缓冲区中的所有事件
   */
  flush(): void {
    if (this.buffer.length === 0) return;

    // 节流检查
    const now = Date.now();
    if (
      this.options.throttle > 0 &&
      now - this.lastEmitTime < this.options.throttle
    ) {
      if (process.env.NODE_ENV === "development") {
        console.log("[Buffer] Throttled, skipping flush");
      }
      return;
    }

    const events = [...this.buffer];
    this.buffer = [];
    this.lastEmitTime = now;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (process.env.NODE_ENV === "development") {
      console.log(`[Buffer] Flushing ${events.length} events`);
    }

    this.onFlush(events);
  }

  /**
   * 清空缓冲区
   */
  clear(): void {
    this.buffer = [];
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (process.env.NODE_ENV === "development") {
      console.log("[Buffer] Cleared");
    }
  }

  /**
   * 获取缓冲区大小
   */
  getSize(): number {
    return this.buffer.length;
  }

  /**
   * 更新配置
   */
  updateOptions(options: Partial<BufferOptions>): void {
    this.options = {
      ...this.options,
      ...options,
    };

    if (process.env.NODE_ENV === "development") {
      console.log("[Buffer] Options updated:", this.options);
    }
  }
}
